import { Component } from '@mycolorway/tao';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';
import formFieldSelectableMixin from 'next/javascripts/mixins/form-field-selectable-mixin';
import { closest } from 'next/javascripts/modules/dom';
import { SelectorFieldResult } from '../selector-field';

Component('tr-combo-select-field-result', {
  mixins: [eventListenerMixin, formFieldSelectableMixin],

  extends: [SelectorFieldResult],

  properties: {
    multiple: {
      type: Boolean,
      default: false,
    },
    inlineSearch: {
      type: Boolean,
      default: false,
    },
  },

  ready() {
    this.selectedOptions = this.multiple ? [] : null;

    if (this.multiple || this.inlineSearch) {
      this.resultChipSet = this.resultWrapper.querySelector('.result-chip-set');
    }

    if (this.inlineSearch) {
      this.searchInput = this.resultWrapper.querySelector('.search-input');
    }

    this.clearResult();
    this.addEventListeners();
  },

  focus() {
    if (this.inlineSearch) {
      this.searchInput.focus();
    } else {
      this.resultWrapper.focus();
    }
  },

  addEventListeners() {
    this.listenTo(this.resultWrapper, {
      click: this.handleClick.bind(this),
      keydown: this.handleKeydown.bind(this),
    });

    if (this.multiple || this.inlineSearch) {
      this.listenTo(this.resultChipSet, {
        'tr-chip-set:removal': this.handleResultChipRemoval.bind(this),
      });
    }

    if (this.inlineSearch) {
      this.listenTo(this.searchInput, {
        input: this.handleSearchInputChange.bind(this),
        keydown: this.handleSearchInputKeydown.bind(this),
      });
    }
  },

  selectOption(option) {
    if (!option || option === this.selectedOptions) return false;
    if (this.multiple) {
      const selectedIndex = this.selectedOptions
        .findIndex(selected => selected.value === option.value);
      if (selectedIndex !== -1) return false;
    }

    if (this.multiple) {
      this.selectedOptions.push(option);
    } else {
      this.unsetSelectedResult(this.selectedOptions);
      this.unsetSelectedOption(this.selectedOptions);
      this.selectedOptions = option;
    }

    this.setSelectedResult(option);
    this.setSelectedOption(option);
    this.selected = true;

    return true;
  },

  unselectOption(option) {
    if (this.multiple) {
      if (!option) return false;

      const selectedIndex = this.selectedOptions
        .findIndex(selected => selected.value === option.value);
      if (selectedIndex === -1) return false;

      this.selectedOptions.splice(selectedIndex, 1);
      this.selected = this.selectedOptions.length > 0;
      this.unsetSelectedResult(option);
      this.unsetSelectedOption(option);
    } else {
      this.clearSelected();
    }

    return true;
  },

  clearSelected() {
    if (this.multiple) {
      this.selectedOptions.forEach(option => this.unsetSelectedOption(option));
      this.selectedOptions = [];
    } else {
      this.unsetSelectedOption(this.selectedOptions);
      this.selectedOptions = null;
    }

    this.clearResult();
    this.selected = false;
  },

  clearResult() {
    if (!this.multiple && !this.inlineSearch) {
      this.resultWrapper.querySelector('.result').textContent = '';
    } else {
      this.resultChipSet.removeChip('.mdc-chip[data-value]');
    }
  },

  activeChanged() {
    if (this.active) {
      this.activeSelectRoot();

      if (this.inlineSearch) {
        this.searchInput.focus();
        const { value } = this.searchInput;
        if (value) this.searchInput.setSelectionRange(0, value.length);
      }
    } else {
      this.deactiveSelectRoot();
      if (this.inlineSearch) this.searchInput.value = '';
      this.namespacedTrigger('deactive');
    }
  },

  selectedChanged() {
    if (this.active) {
      this.restoreFocus();
      if (!this.multiple && this.inlineSearch && this.selected) this.searchInput.value = '';
    } else {
      this.notchOutline(this.selected);
    }

    if (!this.searchInput) return;
    if (this.selected && this.searchInput.placeholder) {
      this.searchInput.dataset.placeholder = this.searchInput.placeholder;
      this.searchInput.placeholder = '';
      return;
    }

    if (!this.selected && this.searchInput.dataset.placeholder) {
      this.searchInput.placeholder = this.searchInput.dataset.placeholder;
      delete this.searchInput.dataset.placeholder;
    }
  },

  handleClearLinkClick() {
    if (this.multiple) return;

    const option = this.selectedOptions;
    this.clearSelected();
    this.namespacedTrigger('unselect', { detail: option });
  },

  restoreFocus() {
    if (this.inlineSearch) {
      this.searchInput.focus();
    } else {
      this.resultWrapper.focus();
    }
  },

  handleClick(event) {
    if (!this.active) {
      this.active = true;
      this.namespacedTrigger('active');
    }

    if (!this.inlineSearch) return;
    if (this.clearable && closest(event.target, '.link-clear')) return;

    if (!document.activeElement || !document.activeElement.isSameNode(this.searchInput)) {
      this.searchInput.focus();
    }
  },

  handleKeydown(event) {
    const arrowUp = event.key === 'ArrowUp' || event.keyCode === 38;
    const arrowDown = event.key === 'ArrowDown' || event.keyCode === 40;
    const isEnter = event.key === 'Enter' || event.keyCode === 13;
    const isEscape = event.key === 'Escape' || event.keyCode === 27;

    if (arrowUp || arrowDown) {
      this.namespacedTrigger('arrowPress', {
        detail: arrowUp ? 'up' : 'down',
      });
      event.preventDefault();
      event.stopPropagation();
    } else if (isEnter && !event.ctrlKey && !event.metaKey) {
      this.namespacedTrigger('enterPress', {
        detail: { originalEvent: event },
      });
    } else if (isEscape) {
      this.active = false;
      this.namespacedTrigger('escapePress');
    }
  },

  handleResultChipRemoval(event) {
    const { value } = event.detail.dataset;
    let option;

    if (this.multiple) {
      option = this.selectedOptions.find(selected => selected.value === value);
    } else {
      option = { ...this.selectedOptions };
    }

    if (option) {
      this.unselectOption(option);
      this.namespacedTrigger('unselect', { detail: option });
    }
  },

  handleSearchInputChange(event) {
    if (!this.multiple && this.selected) {
      const option = this.selectedOptions;
      this.unselectOption(this.selectedOptions);
      this.namespacedTrigger('unselect', { detail: option });
    }

    this.namespacedTrigger('search', { detail: event.target.value });
  },

  handleSearchInputKeydown(event) {
    const isBackspace = event.key === 'Backspace' || event.keyCode === 8;
    if (!isBackspace || !this.selected) return;
    if (this.inlineSearch && this.searchInput.value) return;

    if (this.multiple) {
      const [option] = this.selectedOptions.slice(-1);
      if (option) {
        this.unselectOption(option);
        this.namespacedTrigger('unselect', { detail: option });
      }
    } else if (this.selectedOptions) {
      const option = this.selectedOptions;
      this.unselectOption(this.selectedOptions);
      this.namespacedTrigger('unselect', { detail: option });
    }
  },

  setSelectedResult(option) {
    if (!option) return;

    if (!this.multiple && !this.inlineSearch) {
      this.resultWrapper.querySelector('.result').textContent = option.text;
    } else {
      this.resultChipSet.addChip({
        text: option.text,
        icon: option.data.icon,
        avatar: option.data.avatar,
        data: { value: option.value, ...option.dataAttributes },
      });

      if (this.inlineSearch) {
        this.searchInput.focus();
        const { value } = this.searchInput;
        if (value) this.searchInput.setSelectionRange(0, value.length);
      }
    }
  },

  unsetSelectedResult(option) {
    if (!option) return;

    if (!this.multiple && !this.inlineSearch) {
      this.resultWrapper.querySelector('.result').textContent = '';
    } else {
      this.resultChipSet.removeChip(`.mdc-chip[data-value="${option.value}"]`);
    }
  },

  setSelectedOption(option) {
    if (!option) return;

    const nativeOption = [...this.nativeField.options].find(opt => opt.value === option.value);
    if (nativeOption) {
      nativeOption.selected = true;
    } else {
      this.nativeField.appendChild(this.generateNativeOption(option));
    }
  },

  unsetSelectedOption(option) {
    if (!option) return;

    const nativeOption = [...this.nativeField.selectedOptions]
      .find(native => native.value === option.value);
    if (nativeOption) nativeOption.selected = false;
  },
});
